import React, { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FloppyDisk } from "@phosphor-icons/react/dist/csr/FloppyDisk";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { useEditCourseMutation, useGetCourseCoreQuery } from "@axio/graphql/generated/graphql";
import CustomRichTextEditor from "../../../components/TextEditor/CustomRichTextEditor";
const courseCatalogInfoSchema = z.object({
  price: z.string().optional(),
  description: z.string().min(1, "Description is required"),
  objectives: z.string().min(1, "Objectives are required"),
  targetAudience: z.string().optional(),
});

type CourseCatalogInfoFormData = z.infer<typeof courseCatalogInfoSchema>;

interface AdminCourseCatalogueInfoContainerProps {
  courseId: string;
}

const AdminCourseCatalogueInfoContainer: React.FC<AdminCourseCatalogueInfoContainerProps> = ({ courseId }) => {
  const {
    data: courseData,
    loading,
    error,
    refetch,
  } = useGetCourseCoreQuery({
    variables: {
      id: courseId,
    },
  });

  const { showToast } = useToast();

  const [activelyEditing, setActivelyEditing] = useState(false);

  const course = courseData?.getCourseCore?.data?.[0];

  const [editCourse] = useEditCourseMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm<CourseCatalogInfoFormData>({
    resolver: zodResolver(courseCatalogInfoSchema),
    defaultValues: {
      price: "0",
      description: "",
      objectives: "",
      targetAudience: "",
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        price: course.cost?.toString() || "0",
        description: course.description || "",
        objectives: course.objectives?.join("\n") || "",
        targetAudience: course.targetAudience || "",
      });
    }
  }, [course, reset]);

  const handleSave = handleSubmit(async (formData) => {
    setActivelyEditing(false);
    try {
      await editCourse({
        variables: {
          id: course.id,
          course: {
            cost: formData.price ? Number(formData.price) : null,
            description: formData.description,
            objectives: [formData.objectives],
            targetAudience: formData.targetAudience,
          },
        },
      });

      refetch();
      showToast({
        message: "Course updated successfully",
        type: "success",
      });
      reset(formData);
    } catch (error) {
      console.error("Error saving course:", error);
      showToast({
        message: "Failed to save course changes. Please try again.",
        type: "error",
      });
    }
  });

  if (loading || !course) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Error loading course" size="l" weight="ultraBold" useTranslationText={false} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <CustomText text="Course Price" style={styles.sectionTitle} useTranslationText={false} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              disabled={!course.stripeProductId}
              name="price"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  editable={!!course.stripeProductId}
                  onChangeText={(text) => {
                    const newPrice = Number(text);

                    if (Number.isNaN(newPrice)) {
                      showToast({
                        message: "Invalid price. Please enter a valid number",
                        type: "error",
                      });
                      return;
                    }

                    if (newPrice < 0) {
                      showToast({
                        message: "Invalid price. Please enter a price greater than 0",
                        type: "error",
                      });
                      return;
                    }

                    onChange(newPrice.toString());
                  }}
                  placeholder="0"
                  style={{}}
                  error={errors.price?.message}
                />
              )}
            />
            {errors.price && <CustomText text={errors.price.message} size="xs" weight="ultraBold" style={{}} />}
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Description" style={styles.sectionTitle} useTranslationText={false} />
            <CustomText
              text="This information appears on the course details page in the course catalogue."
              style={styles.subtitle}
              useTranslationText
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="description"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  multiline={true}
                  onChangeText={onChange}
                  placeholder="Course Description"
                  style={{}}
                  error={errors.description?.message}
                  numberOfLines={8}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText
              text="What will the student learn in this course?"
              style={styles.sectionTitle}
              useTranslationText={false}
            />
            <CustomText
              text="This information appears on the course details page in the course catalogue and is best formatted by using bullets."
              style={styles.subtitle}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="objectives"
              render={({ field: { onChange, value } }) => {
                const handleOnChange = (content: string) => {
                  setActivelyEditing(true);
                  onChange(content);
                  setValue("objectives", content, { shouldDirty: true, shouldValidate: false });
                };

                return (
                  <CustomRichTextEditor
                    value={value}
                    onChange={handleOnChange}
                    isSubmitting={false}
                    placeholder="Tell your students a little bit about yourself"
                    activelyEditing={activelyEditing}
                    restrictImageUpload={true}
                  />
                );
              }}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Who is this course for?" style={styles.sectionTitle} useTranslationText={false} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="targetAudience"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  multiline={true}
                  onChangeText={onChange}
                  placeholder="Who is this course for?"
                  style={{}}
                  error={errors.targetAudience?.message}
                  numberOfLines={8}
                />
              )}
            />
          </View>
        </View>

        <View style={styles.footer}>
          <CustomButton
            text="Save"
            styleType="primary"
            size="m"
            bold={true}
            onPress={() => handleSave()}
            leftIcon={<FloppyDisk size={24} color={!isDirty ? "#6B7280" : "#FFFFFF"} />}
            rightIcon={null}
            style={{
              backgroundColor: !isDirty ? "#E5E7EB" : "#3E68FE",
              opacity: !isDirty ? 0.5 : 1,
              width: "100%",
              justifyContent: "center",
            }}
            textStyle={{
              color: !isDirty ? "#6B7280" : "#FFFFFF",
              fontSize: 14,
            }}
            disabled={false}
            textProps={{}}
            useTranslationText={true}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    flex: 1,
    backgroundColor: "#fff",
  },
  content: {
    flexDirection: "column",
    flex: 1,
    padding: 24,
    maxWidth: 800,
    marginHorizontal: "auto",
    width: "100%",
  },
  form: {
    flex: 1,
  },
  formGroup: {
    width: "100%",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
  },
  subtitle: {
    fontSize: 14,
    fontWeight: "400",
    color: "#475467",
    marginTop: 4,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 24,
    width: "100%",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
});

export default AdminCourseCatalogueInfoContainer;
